import { loadingButtonClasses } from '@mui/lab/LoadingButton';
import { common } from '@mui/material/colors';
import { dialogActionsClasses } from '@mui/material/DialogActions';
import { alpha, createTheme as createMuiTheme, darken, getContrastRatio, lighten, } from '@mui/material/styles';
const palette = {
    primary: {
        main: '#3F49E1',
        light: lighten('#3F49E1', 0.5),
        dark: darken('#3F49E1', 0.2),
    },
    secondary: {
        main: '#F5B5FF',
        light: lighten('#F5B5FF', 0.5),
        dark: darken('#F5B5FF', 0.2),
    },
    success: {
        main: '#0AA65B',
    },
    warning: {
        main: '#FFE668',
    },
    error: {
        main: '#E5452F',
    },
    info: {
        main: '#297EFF',
    },
};
const paletteLight = {
    text: {
        primary: '#000000',
        secondary: '#747474',
    },
};
const paletteDark = {
    background: {
        paper: '#212121',
    },
};
const shape = {
    borderRadius: 12,
    borderRadiusSecondary: 6,
};
export const createTheme = (mode, theme = {}) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    const primaryMainColor = (_c = (_b = (_a = theme.palette) === null || _a === void 0 ? void 0 : _a.primary) === null || _b === void 0 ? void 0 : _b.main) !== null && _c !== void 0 ? _c : palette.primary.main;
    const primaryLightColor = lighten((_f = (_e = (_d = theme.palette) === null || _d === void 0 ? void 0 : _d.primary) === null || _e === void 0 ? void 0 : _e.main) !== null && _f !== void 0 ? _f : palette.primary.main, 0.5);
    const primaryDarkColor = darken((_j = (_h = (_g = theme.palette) === null || _g === void 0 ? void 0 : _g.primary) === null || _h === void 0 ? void 0 : _h.main) !== null && _j !== void 0 ? _j : palette.primary.main, 0.2);
    const contrastButtonColor = getContrastRatio(common.white, primaryMainColor) >= 3
        ? common.white
        : common.black;
    return createMuiTheme({
        typography: Object.assign({ fontFamily: 'Inter var, Inter, sans-serif' }, theme.typography),
        palette: Object.assign(Object.assign(Object.assign(Object.assign({ mode }, palette), (mode === 'light' ? paletteLight : paletteDark)), theme.palette), { primary: {
                main: primaryMainColor,
                light: primaryLightColor,
                dark: primaryDarkColor,
            }, secondary: {
                main: (_m = (_l = (_k = theme.palette) === null || _k === void 0 ? void 0 : _k.secondary) === null || _l === void 0 ? void 0 : _l.main) !== null && _m !== void 0 ? _m : palette.secondary.main,
                light: lighten((_q = (_p = (_o = theme.palette) === null || _o === void 0 ? void 0 : _o.secondary) === null || _p === void 0 ? void 0 : _p.main) !== null && _q !== void 0 ? _q : palette.secondary.main, 0.5),
                dark: darken((_t = (_s = (_r = theme.palette) === null || _r === void 0 ? void 0 : _r.secondary) === null || _s === void 0 ? void 0 : _s.main) !== null && _t !== void 0 ? _t : palette.secondary.main, 0.2),
            } }),
        shape: Object.assign(Object.assign({}, shape), theme.shape),
        breakpoints: {
            values: {
                xs: 0,
                sm: 392,
                md: 900,
                lg: 1200,
                xl: 1536,
            },
        },
        components: {
            MuiScopedCssBaseline: {
                styleOverrides: {
                    root: Object.assign(Object.assign({ fontFamily: 'Inter, sans-serif' }, theme.typography), { '@supports (font-variation-settings: normal)': Object.assign({ fontFamily: 'Inter var, sans-serif' }, theme.typography) }),
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: Object.assign({ root: {
                        borderRadius: (_v = (_u = theme.shape) === null || _u === void 0 ? void 0 : _u.borderRadiusSecondary) !== null && _v !== void 0 ? _v : shape.borderRadiusSecondary,
                        textTransform: 'none',
                        fontSize: '1rem',
                        [`&.Mui-disabled, &.Mui-disabled:hover`]: {
                            color: mode === 'light'
                                ? 'rgb(0 0 0 / 56%)'
                                : 'rgb(255 255 255 / 56%)',
                            cursor: 'not-allowed',
                            pointerEvents: 'auto',
                        },
                        [`&.${loadingButtonClasses.loading}.Mui-disabled`]: {
                            backgroundColor: primaryMainColor,
                            color: contrastButtonColor,
                            cursor: 'auto',
                            pointerEvents: 'auto',
                        },
                        [`.${loadingButtonClasses.loadingIndicator}`]: {
                            color: contrastButtonColor,
                        },
                        [`&.${loadingButtonClasses.root}.${loadingButtonClasses.loading}`]: {
                            color: 'transparent',
                        },
                    }, text: {
                        backgroundColor: alpha(primaryMainColor, 0.08),
                        '&:hover': {
                            backgroundColor: alpha(primaryMainColor, 0.12),
                        },
                    }, contained: {
                        '&:hover': {
                            color: contrastButtonColor,
                        },
                    }, sizeMedium: {
                        padding: '10px 14px',
                        [`.${dialogActionsClasses.root} &`]: {
                            padding: '6px 12px',
                        },
                    } }, (mode === 'dark'
                    ? {
                        outlined: {
                            color: primaryLightColor,
                            borderColor: primaryLightColor,
                            '&:hover': {
                                backgroundColor: alpha(primaryLightColor, 0.08),
                                borderColor: primaryLightColor,
                            },
                        },
                        text: {
                            backgroundColor: lighten(paletteDark.background.paper, 0.08),
                            color: common.white,
                            '&:hover': {
                                backgroundColor: lighten(paletteDark.background.paper, 0.12),
                            },
                        },
                    }
                    : {})),
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: 'inherit',
                        '&:hover': {
                            color: 'inherit',
                        },
                    },
                },
            },
            MuiAvatar: Object.assign({ styleOverrides: {
                    root: {
                        height: 32,
                        width: 32,
                    },
                } }, (_w = theme.components) === null || _w === void 0 ? void 0 : _w.MuiAvatar),
            MuiListItemAvatar: {
                styleOverrides: {
                    root: {
                        minWidth: 48,
                    },
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    primary: ({ theme }) => ({
                        fontWeight: '500',
                        fontSize: '1.125rem',
                        lineHeight: '1.2778',
                        color: theme.palette.text.primary,
                    }),
                    secondary: ({ theme }) => ({
                        fontWeight: '400',
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                    }),
                },
            },
        },
    });
};
